import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "@resume-studio/dto";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@resume-studio/ui";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod/v3";

import { useAdminLogin } from "../../services/auth/login";

type FormValues = z.infer<typeof loginSchema>;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loading } = useAdminLogin();

  const form = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "" },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await login(data);

      // useAdminLogin only navigates when the server says "2fa_required" —
      // for a clean success it stays put, so we route the redirect param
      // (or the default overview) here.
      const redirect = searchParams.get("redirect");
      navigate(redirect && redirect.startsWith("/") ? redirect : "/", { replace: true });
    } catch {
      // Toast is handled by the axios interceptor; reset only the password
      // so the operator doesn't have to retype the email on a typo.
      form.resetField("password");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
      <Helmet>
        <title>Sign in · Resume Studio Admin</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck className="size-4" />
          </span>
          <div className="leading-none">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-subtle">
              Resume Studio
            </p>
            <p className="mt-1 text-[14px] font-semibold text-foreground">Admin console</p>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-8">
          <h1 className="text-[20px] font-semibold tracking-tight text-foreground">
            Sign in to continue
          </h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Use your Resume Studio admin account. Non-admin sign-ins are rejected here.
          </p>

          <Form {...form}>
            <form className="mt-6 flex flex-col gap-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                name="identifier"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[12px] font-medium text-foreground">Email</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="username"
                        placeholder="you@resumestudio.io"
                        className="h-9 border-border bg-background text-[13px] focus-visible:ring-primary"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                name="password"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-[12px] font-medium text-foreground">
                      Password
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        className="h-9 border-border bg-background text-[13px] focus-visible:ring-primary"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={loading}
                className="mt-2 h-9 gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Sign in
                <ArrowRight className="size-3.5" />
              </Button>
            </form>
          </Form>
        </div>

        <p className="mt-6 text-center text-[11px] uppercase tracking-[0.16em] text-subtle">
          Internal access only · admin.resumestudio.io
        </p>
      </div>
    </div>
  );
};
